import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, Plus, X, Check, Layers } from 'lucide-react';
import api from '../services/api';
import AddBoardPatternModal from './AddBoardPatternModal';

const BoardPatternSelector = ({ 
  value, 
  onChange, 
  patterns = [], 
  onRefresh,
  placeholder = 'Selecionar padrão...' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const selectedPattern = patterns.find(p => p.id === value);

  const filteredPatterns = patterns.filter(pattern => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      pattern.description?.toLowerCase().includes(term) ||
      pattern.material_type?.toLowerCase().includes(term) ||
      pattern.reference?.toLowerCase().includes(term) ||
      pattern.color?.toLowerCase().includes(term)
    );
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (pattern) => {
    onChange(pattern.id, pattern);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null, null);
  };

  const handleCreateSuccess = (newPattern) => {
    if (onRefresh) {
      onRefresh();
    }
    onChange(newPattern.id, newPattern);
    setShowCreateModal(false);
  };

  const displayText = selectedPattern 
    ? `${selectedPattern.description} (${selectedPattern.thickness}mm)`
    : placeholder;

  return (
    <>
      <div className="relative" ref={containerRef}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`
              flex-1 flex items-center justify-between px-3 py-1.5 border rounded-lg text-sm
              transition-colors min-w-[180px]
              ${selectedPattern 
                ? 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100' 
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }
            `}
          >
            <span className="truncate flex items-center gap-2">
              {selectedPattern ? (
                <>
                  <Layers className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                  <span className="truncate">{displayText}</span>
                </>
              ) : (
                <span className="text-gray-400 truncate">{placeholder}</span>
              )}
            </span>
            <div className="flex items-center gap-1">
              {selectedPattern && (
                <span
                  onClick={handleClear}
                  className="p-0.5 hover:bg-blue-200 rounded cursor-pointer text-blue-600"
                >
                  <X className="w-3.5 h-3.5" />
                </span>
              )}
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </div>
          </button>

          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="px-2.5 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-1.5 text-sm font-medium"
            title="Novo padrão de chapa"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Novo</span>
          </button>
        </div>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
            <div className="p-2 border-b border-gray-100 bg-gray-50/50">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar padrão..."
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                />
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">
                  <div className="w-5 h-5 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin mx-auto mb-2" />
                  Carregando...
                </div>
              ) : filteredPatterns.length === 0 ? (
                <div className="px-4 py-6 text-center">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Search className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 mb-3">
                    {searchTerm ? 'Nenhum padrão encontrado' : 'Nenhum padrão cadastrado'}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpen(false);
                      setShowCreateModal(true);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 mx-auto"
                  >
                    <Plus className="w-4 h-4" />
                    Cadastrar novo padrão
                  </button>
                </div>
              ) : (
                <div className="py-1">
                  {filteredPatterns.map(pattern => (
                    <button
                      key={pattern.id}
                      type="button"
                      onClick={() => handleSelect(pattern)}
                      className={`
                        w-full px-3 py-2.5 text-left hover:bg-gray-50 transition-colors
                        flex items-start gap-3
                        ${selectedPattern?.id === pattern.id ? 'bg-blue-50' : ''}
                      `}
                    >
                      <div 
                        className="w-8 h-8 rounded border-2 flex-shrink-0 mt-0.5"
                        style={{ 
                          backgroundColor: pattern.color_hex || '#E5E7EB',
                          borderColor: pattern.color_hex ? `${pattern.color_hex}40` : '#E5E7EB'
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {pattern.description}
                          </span>
                          {selectedPattern?.id === pattern.id && (
                            <Check className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          )}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                          <span>{pattern.material_type}</span>
                          <span>•</span>
                          <span>{pattern.thickness}mm</span>
                          {pattern.reference && (
                            <>
                              <span>•</span>
                              <span className="truncate">{pattern.reference}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {filteredPatterns.length > 0 && (
              <div className="px-3 py-2 border-t border-gray-100 bg-gray-50/50">
                <button
                  type="button"
                  onClick={() => {
                    setIsOpen(false);
                    setShowCreateModal(true);
                  }}
                  className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1.5 py-1.5 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Cadastrar novo padrão
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <AddBoardPatternModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
    </>
  );
};

export default BoardPatternSelector;
